import React from 'react';
import { Link } from 'react-router-dom';
import styles from './Pages.module.css';

const Documentation = () => {
  return (
    <div className={styles.pageContainer}>
      <div className={styles.pageHeader}>
        <div className={styles.container}>
          <Link to="/" className={styles.backLink}>← Back to Home</Link>
          <h1>Documentation</h1>
        </div>
      </div>
      
      <div className={styles.pageContent}>
        <div className={styles.container}>
          <div className={styles.docNavigation}>
            <ul className={styles.docNavLinks}>
              <li><a href="#introduction">Introduction</a></li>
              <li><a href="#getting-started">Getting Started</a></li>
              <li><a href="#staking">Staking</a></li>
              <li><a href="#unstaking">Unstaking</a></li>
              <li><a href="#rewards">Rewards</a></li>
              <li><a href="#faq">FAQ</a></li>
              <li><a href="#security">Security</a></li>
              <li><a href="#glossary">Glossary</a></li>
            </ul>
          </div>
          
          <div className={styles.docContent}>
            <section id="introduction" className={styles.section}>
              <h2>Introduction</h2>
              <p>
                Welcome to the NexusStake documentation! Here you'll find all the information you need
                to start staking, earn rewards, and effectively manage your assets.
              </p>
              <p>
                Staking is the process of locking your cryptocurrency assets to support blockchain network
                operations while earning passive income. Unlike mining, staking doesn't require powerful hardware
                or significant energy consumption.
              </p>
              <p>
                Our platform offers a secure, transparent, and user-friendly way to stake your tokens and
                earn competitive rewards. The annual percentage yield (APY) for staking is currently set at 12%,
                making it an attractive option for passive income generation.
              </p>
            </section>
            
            <section id="getting-started" className={styles.section}>
              <h2>Getting Started</h2>
              <h3>Requirements</h3>
              <p>To start staking, you'll need:</p>
              <ul>
                <li>A cryptocurrency wallet compatible with our platform (MetaMask, Trust Wallet, etc.)</li>
                <li>Sufficient tokens for staking</li>
                <li>A small amount of ETH to pay for network transaction fees</li>
              </ul>
              
              <h3>Connecting Your Wallet</h3>
              <ol>
                <li>Click the "Connect Wallet" button in the top right corner of the homepage</li>
                <li>Select your preferred wallet from the list</li>
                <li>Confirm the connection in your wallet's interface</li>
                <li>After successful connection, you'll see your address and balance in the interface</li>
              </ol>
              
              <div className={styles.note}>
                <strong>Note:</strong> For security reasons, always verify you're connecting to the correct website.
                Our official URL is <strong>nexusstake.com</strong>.
              </div>
            </section>
            
            <section id="staking" className={styles.section}>
              <h2>Staking</h2>
              <p>The staking process on our platform is simple and intuitive:</p>
              <ol>
                <li>
                  <strong>Prepare tokens:</strong> Ensure you have sufficient tokens in your wallet
                  and a small amount of ETH to pay for transaction fees.
                </li>
                <li>
                  <strong>Enter staking amount:</strong> In the staking section, specify the number of tokens
                  you want to lock.
                </li>
                <li>
                  <strong>Confirm permission:</strong> Before the first staking, you need to grant the smart contract
                  permission to use your tokens. This is a one-time operation for security.
                </li>
                <li>
                  <strong>Confirm staking:</strong> Click the "Stake" button and confirm the transaction in your wallet.
                </li>
                <li>
                  <strong>Wait for confirmation:</strong> After the transaction is confirmed on the blockchain, your tokens
                  will be locked, and you'll start earning rewards.
                </li>
              </ol>
              
              <div className={styles.note}>
                <strong>Note:</strong> The minimum staking amount is 100 tokens. There is no maximum limit.
              </div>
              
              <h3>Staking Rewards Calculation</h3>
              <p>
                Staking rewards are calculated based on the following formula:
              </p>
              <div className={styles.codeBlock}>
                Daily Rewards = (Staked Amount × APY) ÷ 365
              </div>
              <p>
                For example, if you stake 1,000 tokens with a 12% APY, your daily rewards would be:
                (1,000 × 0.12) ÷ 365 = 0.329 tokens per day.
              </p>
            </section>
            
            <section id="unstaking" className={styles.section}>
              <h2>Unstaking</h2>
              <p>
                You can withdraw your locked tokens at any time. However, keep in mind that you'll stop
                receiving staking rewards when you do.
              </p>
              <ol>
                <li>
                  <strong>Go to the staking section:</strong> Navigate to the appropriate section on the main page.
                </li>
                <li>
                  <strong>Enter withdrawal amount:</strong> Specify the number of tokens you want to unlock.
                </li>
                <li>
                  <strong>Confirm unstaking:</strong> Click the "Unstake" button and confirm the transaction in your wallet.
                </li>
                <li>
                  <strong>Wait for confirmation:</strong> After the transaction is confirmed on the blockchain, your tokens
                  will be returned to your wallet.
                </li>
              </ol>
              
              <div className={styles.warning}>
                <strong>Important:</strong> Depending on network congestion, the unstaking process may take from a few
                minutes to several hours.
              </div>
            </section>
            
            <section id="rewards" className={styles.section}>
              <h2>Rewards</h2>
              <p>
                For participating in staking, you receive rewards in the form of additional tokens. The reward amount
                depends on several factors:
              </p>
              <ul>
                <li><strong>Number of locked tokens:</strong> The more tokens you stake, the higher your rewards.</li>
                <li><strong>Staking duration:</strong> Rewards accrue with each new block, approximately every 15 seconds.</li>
                <li><strong>Current Annual Percentage Yield (APY):</strong> The rate may vary depending on the total number of tokens staked in the system.</li>
              </ul>
              
              <h3>Claiming Rewards</h3>
              <p>
                You can claim your accumulated rewards at any time by clicking the "Claim Rewards"
                button in the staking section. The rewards will be sent to your wallet after the transaction is confirmed.
              </p>
              
              <div className={styles.tip}>
                <strong>Tip:</strong> To maximize returns, consider reinvesting your earned rewards
                back into staking, allowing you to benefit from compound interest.
              </div>
              
              <h3>Compounding Effects</h3>
              <p>
                Reinvesting your rewards can significantly increase your returns over time. Below is an example of how compounding
                affects your earnings when staking 1,000 tokens at 12% APY:
              </p>
              <div className={styles.tableContainer}>
                <table className={styles.cookieTable}>
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>Without Compounding</th>
                      <th>With Monthly Compounding</th>
                      <th>Difference</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>1</td>
                      <td>1,120 tokens</td>
                      <td>1,126.83 tokens</td>
                      <td>+6.83 tokens</td>
                    </tr>
                    <tr>
                      <td>3</td>
                      <td>1,360 tokens</td>
                      <td>1,430.77 tokens</td>
                      <td>+70.77 tokens</td>
                    </tr>
                    <tr>
                      <td>5</td>
                      <td>1,600 tokens</td>
                      <td>1,816.70 tokens</td>
                      <td>+216.70 tokens</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
            
            <section id="faq" className={styles.section}>
              <h2>Frequently Asked Questions</h2>
              
              <div className={styles.faqItem}>
                <h3>How secure is staking on your platform?</h3>
                <p>
                  Security is our priority. Our smart contracts have undergone security audits, and we use
                  best practices to protect user funds. However, as with any blockchain operation,
                  there are certain risks. We recommend reviewing our <a href="#security">security section</a>
                  for additional information.
                </p>
              </div>
              
              <div className={styles.faqItem}>
                <h3>Can I stake partially, rather than my entire balance at once?</h3>
                <p>
                  Yes, you can stake any amount above the minimum threshold (100 tokens). You can also
                  make additional stakes at any time.
                </p>
              </div>
              
              <div className={styles.faqItem}>
                <h3>What happens if I want to unstake before the lock-up period ends?</h3>
                <p>
                  Currently, our platform doesn't have a fixed lock-up period, and you can unstake
                  at any time without penalties. However, you'll stop earning rewards on the unstaked tokens.
                </p>
              </div>
              
              <div className={styles.faqItem}>
                <h3>Are there fees for staking or unstaking?</h3>
                <p>
                  The platform doesn't charge fees for staking or unstaking. However, you'll need to pay
                  standard Ethereum network fees (gas) for performing transactions.
                </p>
              </div>
              
              <div className={styles.faqItem}>
                <h3>How often is my staking and reward information updated?</h3>
                <p>
                  Your staking and accumulated rewards information is updated in real-time. However, during
                  periods of high network congestion, there might be a slight delay in displaying current data.
                </p>
              </div>
              
              <div className={styles.faqItem}>
                <h3>What is the difference between APY and APR?</h3>
                <p>
                  APR (Annual Percentage Rate) is the simple interest rate without accounting for compounding effects.
                  APY (Annual Percentage Yield) includes the effect of compounding, providing a more accurate
                  representation of what you'll earn over time if you reinvest your rewards.
                </p>
              </div>
            </section>
            
            <section id="security" className={styles.section}>
              <h2>Security</h2>
              <p>
                We take the following measures to ensure the security of your funds:
              </p>
              <ul>
                <li>
                  <strong>Smart contract audits:</strong> Our smart contracts have undergone independent security audits.
                  Audit reports are available <a href="#audit-reports">here</a>.
                </li>
                <li>
                  <strong>Open-source code:</strong> The source code of our smart contracts is open and available for inspection.
                </li>
                <li>
                  <strong>Multi-layered protection:</strong> A multi-layered security system is used to protect against unauthorized access.
                </li>
                <li>
                  <strong>Regular updates:</strong> We regularly update our platform and smart contracts
                  to address potential vulnerabilities.
                </li>
              </ul>
              
              <h3>Security Recommendations</h3>
              <p>For maximum protection of your funds, we recommend:</p>
              <ul>
                <li>Using hardware wallets (Ledger, Trezor) for storing cryptocurrency</li>
                <li>Never sharing private keys or recovery phrases</li>
                <li>Always verifying addresses and transaction amounts before confirming</li>
                <li>Regularly updating wallet software</li>
                <li>Enabling two-factor authentication wherever possible</li>
              </ul>
              
              <h3>Risks</h3>
              <p>
                While we work diligently to provide a secure platform, it's important to be aware of the potential risks:
              </p>
              <ul>
                <li>
                  <strong>Smart contract risks:</strong> Despite audits, there's always a possibility of undetected vulnerabilities.
                </li>
                <li>
                  <strong>Market volatility:</strong> The value of staked tokens may fluctuate based on market conditions.
                </li>
                <li>
                  <strong>Regulatory changes:</strong> Changes in cryptocurrency regulations may affect the platform's operations.
                </li>
              </ul>
              <p>
                We recommend only staking amounts you can afford to lose and diversifying your investments across different assets.
              </p>
            </section>
            
            <section id="glossary" className={styles.section}>
              <h2>Glossary</h2>
              <dl className={styles.glossary}>
                <dt>APY (Annual Percentage Yield)</dt>
                <dd>
                  Annual percentage yield accounting for compound interest. Shows how much you can earn
                  over a year assuming rewards are reinvested.
                </dd>
                
                <dt>Blockchain</dt>
                <dd>
                  A distributed database that maintains a continuously growing list of records, called blocks,
                  which are linked and secured using cryptography.
                </dd>
                
                <dt>Gas</dt>
                <dd>
                  A unit of measurement used to determine the cost of performing operations on the Ethereum network.
                  Each transaction requires a certain amount of gas to execute.
                </dd>
                
                <dt>MetaMask</dt>
                <dd>
                  A popular browser extension that allows users to interact with the Ethereum blockchain
                  and manage their cryptocurrency assets.
                </dd>
                
                <dt>Smart Contract</dt>
                <dd>
                  Self-executing contracts with the terms of the agreement directly written into code. They run
                  on blockchains and automatically execute when predetermined conditions are met.
                </dd>
                
                <dt>Token</dt>
                <dd>
                  A digital asset that represents a certain value or utility on a blockchain platform.
                </dd>
                
                <dt>Wallet</dt>
                <dd>
                  A digital tool that allows users to store, manage, and trade cryptocurrencies. It contains
                  private keys that give access to the coins.
                </dd>
              </dl>
            </section>
            
            <div className={styles.lastUpdated}>
              Last updated: May 1, 2023
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation; 